import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { usePageMeta } from '@/lib/pageMeta';
import { ArrowLeft, Coffee, MapPin, PenLine, MessageSquareWarning } from 'lucide-react';
import RequestChangeModal from '@/components/coffee/RequestChangeModal';
import StarRating from '@/components/coffee/StarRating';
import ReviewCard from '@/components/coffee/ReviewCard';
import ShareButtons from '@/components/coffee/ShareButtons';
import CafeInfo from '@/components/cafe/CafeInfo';
import WriteReviewModal from '@/components/coffee/WriteReviewModal';
import EditReviewModal from '@/components/coffee/EditReviewModal';
import ThankYouModal from '@/components/coffee/ThankYouModal';

// Every coffee page used to share the shell's title and description, which is
// enough on its own for Google to treat them as one page. These give each one
// a description of its own to go with its canonical URL.
function metaTitle(coffee) {
  return `${coffee.name} at ${coffee.cafe_name} | Signature Coffee Note`;
}

function metaDescription(coffee) {
  const where = [coffee.cafe_name, coffee.cafe_area || coffee.city].filter(Boolean).join(', ');
  const details = [coffee.brew_method, coffee.bean_origin, coffee.roaster_name]
    .filter(Boolean)
    .join(' · ');
  return [
    `${coffee.name} is a signature coffee at ${where}.`,
    details && `${details}.`,
    'Read reviews and ratings on Signature Coffee Note.',
  ]
    .filter(Boolean)
    .join(' ');
}

const FIELD_LABEL = {
  brew_method: 'Brew Method',
  processing_method: 'Processing',
  bean_origin: 'Origin',
  varietal: 'Varietal',
  roaster_name: 'Roaster',
  availability: 'Availability',
};

export default function CoffeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coffee, setCoffee] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewerCounts, setReviewerCounts] = useState({});
  const [reviewerAvatars, setReviewerAvatars] = useState({});
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [showRequestChange, setShowRequestChange] = useState(false);
  const { user } = useAuth();
  const [showThankYou, setShowThankYou] = useState(false);

  // A coffee that does not exist still answers 200 through the SPA rewrite, so
  // it has to say noindex rather than rely on the status code.
  usePageMeta({
    title: coffee ? metaTitle(coffee) : undefined,
    description: coffee ? metaDescription(coffee) : undefined,
    image: coffee?.photo_url,
    index: loading || coffee ? undefined : false,
  });

  useEffect(() => {
    Promise.all([
      api.entities.Coffee.get(id),
      api.entities.Review.filter({ coffee_id: id }, '-created_date'),
    ]).then(async ([found, revs]) => {
      setCoffee(found || null);
      setReviews(revs);
      setLoading(false);
      const reviewerIds = [...new Set(revs.map((r) => r.reviewer_id).filter(Boolean))];
      if (reviewerIds.length > 0) {
        // Only the reviewers on this page — the Base44 version fetched every
        // user in the database to build the same two lookups.
        const users = await api.entities.User.filter({ id: reviewerIds });
        const counts = {};
        const avatars = {};
        users.forEach((u) => { counts[u.id] = u.review_count || 0; avatars[u.id] = u.avatar_url || ''; });
        setReviewerCounts(counts);
        setReviewerAvatars(avatars);
      }
    });
  }, [id]);

  // average_rating and review_count are maintained by the reviews trigger, in
  // the same transaction as the write. Re-reading is all the client has to do;
  // there is no recalculation call to forget any more.
  const refreshCoffee = useCallback(async () => {
    const [found, revs] = await Promise.all([
      api.entities.Coffee.get(id),
      api.entities.Review.filter({ coffee_id: id }, '-created_date'),
    ]);
    setReviews(revs);
    setCoffee(found || null);
  }, [id]);

  const handleDeleteReview = async (reviewId) => {
    await api.entities.Review.delete(reviewId);
    await refreshCoffee();
  };

  // Real-time subscription for reviews (create, update, delete).
  // REPLICA IDENTITY FULL on reviews means a delete carries the whole row, so
  // coffee_id is present on every event — the Base44 payload dropped it on
  // deletes, which is why this used to refresh on any review anywhere.
  useEffect(() => {
    const unsubscribe = api.entities.Review.subscribe((event) => {
      if (event.data?.coffee_id === id) refreshCoffee();
    });
    return () => unsubscribe();
  }, [id, refreshCoffee]);

  const handleReviewSubmitted = async () => {
    setShowReview(false);
    await refreshCoffee();
    setShowThankYou(true);
  };

  const handleReviewUpdated = async () => {
    setEditingReview(null);
    await refreshCoffee();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!coffee) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-muted-foreground">Coffee not found.</p>
        <Link to="/search" className="text-primary text-sm hover:underline">Back to search</Link>
      </div>
    );
  }

  const fields = [
    ['brew_method', coffee.brew_method],
    ['processing_method', coffee.processing_method],
    ['bean_origin', coffee.bean_origin],
    ['varietal', coffee.varietal],
    ['roaster_name', coffee.roaster_name],
    ['availability', coffee.availability],
  ].filter(([, v]) => v);

  return (
    <div className="min-h-screen pb-8">
      {/* Back button */}
      <div className="px-4 pt-4 max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft size={16} />
          Back
        </button>
      </div>

      <div className="px-4 pb-6 max-w-5xl mx-auto space-y-6">
        {/* Hero: 2-column layout on desktop, stacked on mobile */}
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left: portrait photo */}
          <div className="w-full md:w-[280px] shrink-0">
            <div className="aspect-[3/4] bg-muted rounded-2xl overflow-hidden border border-border">
              {coffee.photo_url ? (
                <img src={coffee.photo_url} alt={coffee.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Coffee size={56} className="text-muted-foreground opacity-20" />
                </div>
              )}
            </div>
          </div>

          {/* Right: info */}
          <div className="flex-1 space-y-4">
            <div className="space-y-1">
              <h1 className="font-heading text-2xl font-bold leading-tight">{coffee.name}</h1>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <MapPin size={13} className="shrink-0" />
                <Link to={`/search`} className="text-sm hover:text-primary transition-colors">
                  {coffee.cafe_name}
                </Link>
              </div>
              {coffee.cafe_area && (
                <p className="text-xs text-muted-foreground pl-[18px]">{coffee.cafe_area}</p>
              )}
            </div>

            {coffee.average_rating ? (
              <div className="flex items-center gap-2">
                <StarRating rating={coffee.average_rating} size={18} />
                <span className="font-semibold text-foreground">{coffee.average_rating.toFixed(1)}</span>
                <span className="text-sm text-muted-foreground">({coffee.review_count || 0} {coffee.review_count === 1 ? 'review' : 'reviews'})</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No reviews yet</p>
            )}

            {/* Recipes from coffee */}
            {coffee.recipes && (
              <div className="bg-secondary/50 rounded-xl px-3 py-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Recipes</p>
                <p className="text-sm text-foreground italic">"{coffee.recipes}"</p>
              </div>
            )}

            {/* Attributes */}
            <div className="grid grid-cols-2 gap-2">
              {fields.map(([key, value]) => (
                <div key={key} className="bg-secondary/50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{FIELD_LABEL[key]}</p>
                  <p className="text-sm font-medium text-foreground mt-0.5">{value}</p>
                </div>
              ))}
            </div>

            {/* Write a review */}
            <button
              onClick={() => {
                if (!user) {
                  api.auth.redirectToLogin(window.location.pathname);
                } else {
                  setShowReview(true);
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-2xl font-semibold text-sm shadow-sm hover:bg-primary/90 active:scale-[0.98] transition-all"
            >
              <PenLine size={16} />
              Write a Review
            </button>

            {/* Request Change */}
            <button
              onClick={() => {
                if (!user) {
                  api.auth.redirectToLogin(window.location.pathname);
                } else {
                  setShowRequestChange(true);
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-3 border border-border rounded-2xl font-medium text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 active:scale-[0.98] transition-all"
            >
              <MessageSquareWarning size={16} />
              Request Change
            </button>

            {/* Share */}
            <ShareButtons coffee={coffee} />
          </div>
        </div>

        {/* Cafe Info from Google Places */}
        
        <section>
          <h2 className="font-heading text-lg font-semibold mb-4">About the Cafe</h2>
          <CafeInfo placeId={coffee.place_id} cafeName={coffee.cafe_name} />
        </section>

        {/* Reviews */}
        <section>
          <h2 className="font-heading text-lg font-semibold mb-4">
            Reviews ({reviews.length})
          </h2>
          {reviews.length > 0 ? (
            <div className="space-y-3">
              {reviews.map((r) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  user={user}
                  isOwner={!!user && r.reviewer_id === user.id}
                  onEdit={() => setEditingReview(r)}
                  onDelete={() => handleDeleteReview(r.id)}
                  showTranslateUI={true}
                  reviewerReviewCount={reviewerCounts[r.reviewer_id]}
                  reviewerAvatarUrl={reviewerAvatars[r.reviewer_id]}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No reviews yet. Be the first to review!</p>
            </div>
          )}
        </section>
      </div>

      {/* Review Modal */}
      {showReview && (
        <WriteReviewModal
          coffee={coffee}
          user={user}
          onClose={() => setShowReview(false)}
          onSubmitted={handleReviewSubmitted}
        />
      )}

      {/* Request Change Modal */}
      {showRequestChange && (
        <RequestChangeModal
          coffee={coffee}
          user={user}
          onClose={() => setShowRequestChange(false)}
          onSubmit={() => setShowRequestChange(false)}
        />
      )}

      {/* Thank You Modal */}
      {showThankYou && <ThankYouModal onClose={() => setShowThankYou(false)} />}

      {/* Edit Review Modal */}
      {editingReview && (
        <EditReviewModal
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSubmitted={handleReviewUpdated}
        />
      )}
    </div>
  );
}